import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { type CardIdType, type CardMapType, type CardType } from '@/defs/card'
import { getCardMapQuery, getCardQuery } from '@/utils/cardUtil'

const EMPTY_CARD_MAP: CardMapType = {}
const EMPTY_CARD_IDS: CardIdType[] = []

type Props = {
  cardIds?: CardIdType[]
  enabled?: boolean
}

export const useCardMap = ({ cardIds: cardIdsInput = EMPTY_CARD_IDS, enabled = true }: Props) => {
  const [wasEverEnabled, setWasEverEnabled] = useState(enabled)
  if (enabled && !wasEverEnabled) setWasEverEnabled(true)

  const mapKey = useMemo(() => cardIdsInput.join(','), [cardIdsInput])
  const queryClient = useQueryClient()

  const cardIds = useMemo(
    () => (wasEverEnabled ? [...new Set(cardIdsInput)].filter(Boolean) : []),
    // NOTE: mapKey serves the purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wasEverEnabled, mapKey],
  )

  const cardIdsRemaining = useMemo(
    () => cardIds.filter((cardId) => queryClient.getQueryData(getCardQuery(cardId).queryKey) === undefined),
    [cardIds, queryClient],
  )

  const query = useMemo(
    () => getCardMapQuery({ cardIds: cardIdsRemaining, queryClient }),
    [queryClient, cardIdsRemaining],
  )

  const { dataUpdatedAt, isError, isLoading } = useQuery({
    enabled: wasEverEnabled && cardIdsRemaining.length > 0,
    gcTime: 0,
    ...query,
  })

  const cardMap = useMemo(() => {
    const map: CardMapType = {}
    cardIds.forEach((cardId) => {
      const card = queryClient.getQueryData<CardType>(getCardQuery(cardId).queryKey)
      if (card) map[cardId] = card
    })
    return Object.keys(map).length > 0 ? map : EMPTY_CARD_MAP
    // NOTE: dataUpdatedAt ensures syncing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardIds, queryClient, dataUpdatedAt])

  return {
    cardMap,
    hasError: isError,
    loading: isLoading,
  }
}
